/**
 * Shared lazy-loader for the Google Maps JS SDK (Places library).
 *
 * Any component that needs `window.google.maps.places` should call
 * `ensureGoogleMaps()` on mount and then either:
 *   - subscribe with `onGoogleMapsReady(cb)` to be notified when the
 *     script finishes loading, OR
 *   - poll `window.google?.maps?.places` themselves.
 *
 * Centralised here (rather than duplicated in each picker) so that:
 *   - We never inject the script tag twice on a single page, even if
 *     several pickers mount simultaneously (we dedupe via the script id
 *     AND a module-level promise).
 *   - Components on routes that don't render the composer's
 *     `AddressAutocomplete` (e.g. the personal Location & Contact
 *     settings section) still get the script loaded.
 *   - There's one place to swap in a different loader / API version.
 *
 * If `REACT_APP_GOOGLE_API_ID` is not set, `ensureGoogleMaps` resolves
 * `false` — callers should degrade to a plain text input rather than
 * throwing.
 */

const SCRIPT_ID = 'sharpenn-google-maps-places'

let loadPromise = null

const isReady = () =>
  typeof window !== 'undefined' && !!window.google?.maps?.places

/**
 * Inject the Maps script if it isn't already present, returning a
 * promise that resolves to `true` when `window.google.maps.places` is
 * available, or `false` if loading is impossible (no API key, no DOM,
 * script error).
 */
export const ensureGoogleMaps = () => {
  if (typeof window === 'undefined') return Promise.resolve(false)
  if (isReady()) return Promise.resolve(true)
  if (loadPromise) return loadPromise

  const apiKey = process.env.REACT_APP_GOOGLE_API_ID
  if (!apiKey) return Promise.resolve(false)

  loadPromise = new Promise((resolve) => {
    // If a prior render injected the tag but the script hasn't finished
    // executing, attach to the existing element rather than adding a
    // second copy (Google warns loudly about duplicate loads).
    const existing = document.getElementById(SCRIPT_ID)
    const script = existing || document.createElement('script')
    if (!existing) {
      script.id = SCRIPT_ID
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`
      script.async = true
      script.defer = true
    }
    const handleLoad = () => resolve(isReady())
    const handleError = () => {
      // Reset so a future call can retry (e.g. user reconnected to
      // network). The injected <script> stays in the DOM but Google's
      // loader is idempotent on the same URL.
      loadPromise = null
      resolve(false)
    }
    script.addEventListener('load', handleLoad, {once: true})
    script.addEventListener('error', handleError, {once: true})
    if (!existing) document.body.appendChild(script)
  })

  return loadPromise
}

export const isGoogleMapsReady = isReady
